/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Heading, Container, Link, Text} from '@chakra-ui/react'
import {Region, regionPropType} from '@salesforce/commerce-sdk-react/components'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'

const productOffers = {
    position: 'relative',

    '::before': {
        position: 'absolute',
        content: '""',
        bg: '#f2f4f4',
        width: '100%',
        height: {base: '100%', md: '45.5%'},
        top: '0',
        left: '0'
    },

    ':hover': {
        textDecoration: 'none'
    },

    '.region': {
        '.container': {
            display: 'flex',
            justifyContent: 'center',
            gap: '5%'
        }
    }
}

/**
 * This layout component displays its children in a 2 row x 1 column grid on mobile
 * and a 1 row x 2 column grid on desktop.
 *
 * @param {componentProps} props
 * @param {regionType []} props.regions - The page designer regions for this component.
 * @param {object} props.data - The data for the component.
 * @param {string} props.typeId - A mapping of typeId's to react components representing the type.
 * @returns {React.ReactElement} - Grid component.
 */
export const PercentageLayout = ({
    regions,
    textHeadline,
    description,
    couponCode,
    exclusionNote,
    categoryLink
}) => {
    const centerSemiBoldHeading = {
        textAlign: 'center',
        fontWeight: 'semibold',
        m: '1em 0 1rem',
        as: 'h3'
    }
    const segment = useSegment()

    return (
        <Container my="2.125rem">
            <Heading sx={centerSemiBoldHeading} fontSize="2xl">
                {textHeadline}
            </Heading>
            <Heading sx={centerSemiBoldHeading} fontSize="lg">
                {description}
            </Heading>
            <Heading sx={centerSemiBoldHeading} fontSize="2xl">
                {couponCode}
            </Heading>
            <Text sx={centerSemiBoldHeading} fontSize="lg">
                {exclusionNote}
            </Text>
            <Link
                href={categoryLink}
                textDecoration="none"
                sx={productOffers}
                onClick={(e) => {
                    // segment
                    segment.sendPanelSelectedEvent(e, {})
                }}
            >
                <Box position={'relative'} mt="1.875rem">
                    <Flex justifyContent={'center'} columns={{base: '0', sm: '2', md: '5'}}>
                        {regions.map((region) => (
                            <Region key={region.id} region={region} />
                        ))}
                    </Flex>
                </Box>
            </Link>
        </Container>
    )
}

PercentageLayout.displayName = 'percentageLayout'

PercentageLayout.propTypes = {
    // Internally Provided
    regions: PropTypes.arrayOf(regionPropType).isRequired,
    textHeadline: PropTypes.string,
    description: PropTypes.string,
    couponCode: PropTypes.string,
    exclusionNote: PropTypes.string,
    categoryLink: PropTypes.string
}

export default PercentageLayout
